import { Component, Inject, OnInit, ViewChild } from '@angular/core';
import { combineAll, DcBaseComponent } from '@datachain/ui-sdk/common';
import { AppModalService } from '@datachain/ui-sdk/components/app-modal';
import { PanelAction } from '@datachain/ui-sdk/components/app-panel';
import { AvatarEntity } from '@datachain/ui-sdk/components/avatar';
import { IBreadCrumbEntry } from '@datachain/ui-sdk/components/breadcrumb';
import { AppIcons } from '@datachain/ui-sdk/components/dc-icon';
import { AppTagItemEntity } from '@datachain/ui-sdk/components/dc-types';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { DxDataGridComponent } from 'devextreme-angular';
import {
	CellHoverChangedEvent,
	SelectionChangedEvent,
} from 'devextreme/ui/data_grid';
import { OrderedSet } from 'immutable';
import {
	BehaviorSubject,
	debounceTime,
	firstValueFrom,
	Observable,
	of,
	ReplaySubject,
	takeUntil,
	tap,
	timer,
} from 'rxjs';
import {
	checkStringEquality,
	DcDxGrids,
	DEFAULT_PAGE_SIZE,
	DEFAULT_PAGE_SIZES,
	DxFilterElem,
	DxHeaderFilterItem,
	DxOperations,
	HeaderFilterDefaultMapper,
	NoopValue,
} from 'src/app/core/dx-types';

import {
	DxListsStorage,
	IDxList,
} from '../../../core/services/dx-lists.storage';
import { goToGdHome, goToProjects } from '../../../core/store';
import { ActionIcons } from '../../../ui/app-actions.icons';
import { ComponentIcons } from '../../../ui/app-components.icons';
import { DcIcons } from '../../../ui/app-dc.icons';
import { GdIcons } from '../../../ui/app-gd.icons';
import { DcLoaders } from '../../../ui/app-loaders';
import { ActionIconsInjectable } from '../../../ui/ui.module';
import {
	bulkDeleteConnectorsFail,
	bulkDeleteConnectorsSuccess,
	ConnectorSelector,
	deleteConnectorFail,
	deleteConnectorSuccess,
	initConnectorList,
	navigateToConnector,
	navigateToConnectorCreate,
	navigateToConnectorDuplicate,
	navigateToDepotFromConnector,
	refreshConnectorList,
	toggleConnectorStatus,
} from '../../store';
import {
	ConnectorAction,
	ConnectorActionPopupComponent,
	IConnectorActionModalData,
} from '../connector-action-popup/connector-action-popup.component';
import { ConnectorItemEntity } from './connector-item.entity';
import { ConnectorListEntity } from './connector-list.entity';

type RowAction = {
	description: string;
	actionName: string;
	icon: string;
	action: (...args: Array<unknown>) => void;
};

@Component({
	selector: 'app-connector-list',
	templateUrl: './connector-list.component.html',
	styleUrls: ['./connector-list.component.scss'],
	inputs: ['projectLabel'],
})
export class ConnectorListComponent extends DcBaseComponent implements OnInit {
	protected readonly GdIcons = GdIcons;
	protected readonly DcIcons = DcIcons;
	protected readonly DcLoaders = DcLoaders;
	protected readonly ComponentIcons = ComponentIcons;
	protected readonly pageSize = DEFAULT_PAGE_SIZE;
	protected readonly allowPageSizes = DEFAULT_PAGE_SIZES;

	public paths: Array<IBreadCrumbEntry> = [];
	public ActionIcons = ActionIcons;
	public DxOperations = DxOperations;
	public panelActions: Array<PanelAction> = [];
	public currentlyHovered = -1;
	public toggleActions = false;
	public selectionCount = 0;
	public readonly disabledRowActions: Array<RowAction> = [
		{
			actionName: 'duplicate',
			description: $localize`:i18n=@@connector.list.action.duplicate:`,
			action: (item: unknown): void => {
				const connector = item as ConnectorItemEntity;
				this.duplicateConnector(connector);
			},
			icon: ActionIcons.DuplicateElement,
		},
		{
			actionName: 'activate',
			description: $localize`:i18n=@@general.action.activate:`,
			action: (item: unknown): void => {
				const connector = item as ConnectorItemEntity;
				this.toggleActivationStatus(connector);
			},
			icon: ActionIcons.ActivateElement,
		},
	];
	public readonly activeRowActions: Array<RowAction> = [
		{
			actionName: 'duplicate',
			description: $localize`:i18n=@@connector.list.action.duplicate:`,
			action: (item: unknown): void => {
				const connector = item as ConnectorItemEntity;
				this.duplicateConnector(connector);
			},
			icon: ActionIcons.DuplicateElement,
		},
		{
			actionName: 'deactivate',
			description: $localize`:i18n=@@general.action.deactivate:`,
			action: (item: unknown): void => {
				const connector = item as ConnectorItemEntity;
				this.toggleActivationStatus(connector);
			},
			icon: ActionIcons.DeactivateElement,
		},
	];

	public tagsHeaderFilter: Array<DxHeaderFilterItem> = [];
	public modifiedByHeaderFilter: Array<DxHeaderFilterItem> = [];
	public actionByHeaderFilter: Array<DxHeaderFilterItem> = [];

	public isImportedHeaderFilter = [
		{
			text: $localize`:i18n=@@connector.list.column.imported:`,
			value: ['isImported', '=', true],
		},
		{
			text: $localize`:i18n=@@connector.list.column.unimported:`,
			value: ['isImported', '=', false],
		},
	];

	public actifHeaderFilter = [
		{
			text: $localize`:i18n=@@general.column.actif:`,
			value: ['isActive', '=', true],
		},
		{
			text: $localize`:i18n=@@general.column.unactif:`,
			value: ['isActive', '=', false],
		},
	];

	public vo$: Observable<{
		isFirstLoad: boolean;
		isLoading: boolean;
		list: ConnectorListEntity;
	}>;

	public loadState = (): IDxList | undefined =>
		this.dxStorage.getListState(this.dxLocalStorageKey);

	public saveState = (state: IDxList): void => {
		this.dxStorage.setListState(this.dxLocalStorageKey, {
			...state,
			searchText: '',
		});
	};

	@ViewChild(DxDataGridComponent)
	private readonly grid: DxDataGridComponent | null = null;

	private readonly connectorListSubject =
		new ReplaySubject<ConnectorListEntity>(1);

	private readonly firstLoadSubject = new BehaviorSubject<boolean>(true);

	public constructor(
		private readonly store: Store,
		private readonly connectorSelector: ConnectorSelector,
		private readonly actions$: Actions,
		private readonly modalService: AppModalService,
		@Inject(ActionIconsInjectable) private readonly actionIcons: AppIcons,
		private readonly dxStorage: DxListsStorage
	) {
		super();
		this.cmpId = 'connector-list';
		this.dxLocalStorageKey = DcDxGrids.ConnectorListStandalone;

		this.vo$ = combineAll({
			isFirstLoad: this.firstLoadSubject,
			isLoading: this.connectorSelector.getLoading$(),
			list: this.connectorListSubject,
		});
	}

	public ngOnInit(): void {
		super.ngOnInit();
		this.store.dispatch(initConnectorList());

		this.handleSubscriptions();

		combineAll({
			projectLabel: this.toObservable<string>('projectLabel'),
		})
			.pipe(
				takeUntil(this.onDestroy$),
				tap(({ projectLabel }) => {
					this.paths = [
						{
							label: $localize`:i18n=@@general.projects:`,
							isActive: true,
							callback: (): void => this.store.dispatch(goToProjects()),
						},
						{
							label: projectLabel,
							isActive: true,
							callback: (): void => this.store.dispatch(goToGdHome()),
						},
						{
							label: $localize`:i18n=@@connector.list.title:`,
							isActive: false,
							callback: (): void => {},
						},
					];
				})
			)
			.subscribe();

		of(null)
			.pipe(
				takeUntil(this.onDestroy$),
				tap(() => this.bootstrapIcons())
			)
			.subscribe();

		this.connectorSelector
			.getConnectorList$()
			.pipe(
				takeUntil(this.onDestroy$),
				debounceTime(320),
				tap((l) => {
					this.tagsHeaderFilter = l
						.extractUniqTags(NoopValue)
						.map(HeaderFilterDefaultMapper)
						.toArray();
					this.modifiedByHeaderFilter = l
						.extractModifiers(NoopValue)
						.map(HeaderFilterDefaultMapper)
						.toArray();
					this.actionByHeaderFilter = l
						.extractActionCreators(NoopValue)
						.map(HeaderFilterDefaultMapper)
						.toArray();

					this.connectorListSubject.next(l);
					if (this.grid) {
						this.grid.selectedRowKeys = [];
					}
				})
			)
			.subscribe();
	}

	public refresh(): void {
		this.firstLoadSubject.next(true);
		this.grid?.instance.beginCustomLoading('');
		this.store.dispatch(refreshConnectorList());
	}

	public search(searchTerm: string): void {
		if (this.grid === null) {
			return;
		}
		this.grid.instance.searchByText(searchTerm.trim());
	}

	public onSelectionChange($event: SelectionChangedEvent): void {
		this.toggleActions = $event.selectedRowsData.length > 0;
		this.selectionCount = $event.selectedRowsData.length;
	}

	public async onGridContentReady(): Promise<void> {
		const firstLoad = await firstValueFrom(this.firstLoadSubject);

		if (firstLoad) {
			await firstValueFrom(
				timer(1000).pipe(
					tap(() => {
						this.firstLoadSubject.next(false);
						this.grid?.instance.endCustomLoading();
					})
				)
			);
		}
	}

	public rowMenuOpen($event: MouseEvent, rowIndex: number): void {
		$event.stopPropagation();
		// fake show menu button
		setTimeout(() => {
			this.currentlyHovered = rowIndex;
		}, 300);
	}

	public onCellHover(event: CellHoverChangedEvent): void {
		if (event.eventType === 'mouseover') {
			this.currentlyHovered = event.rowIndex;
		} else {
			this.currentlyHovered = -1;
		}
	}

	public createNewConnector(): void {
		this.store.dispatch(navigateToConnectorCreate());
	}

	public navigateToConnector(connector: ConnectorItemEntity): void {
		this.store.dispatch(
			navigateToConnector({
				id: connector.id,
				connectorType: connector.type,
			})
		);
	}

	public duplicateConnector(
		connector: ConnectorItemEntity,
		event?: Event
	): void {
		if (event) {
			event.stopPropagation();
		}

		this.store.dispatch(
			navigateToConnectorDuplicate({
				id: connector.id,
				connectorType: connector.type,
			})
		);
	}

	public createDepotFromConnector(
		event: Event,
		connector: ConnectorItemEntity
	): void {
		event.stopPropagation();
		this.store.dispatch(
			navigateToDepotFromConnector({
				id: connector.id,
			})
		);
	}

	public deleteConnectorRow(
		event: Event,
		connector: ConnectorItemEntity
	): Promise<void> {
		event.stopPropagation();
		return this.deleteSelected(connector);
	}

	public calculateTagsHeaderFilterExpression(
		value: string,
		filterOperation: string,
		target: string
	): unknown {
		const column = this as any;
		const selector = (data: ConnectorItemEntity): boolean => {
			const tagsSet = column.calculateCellValue(
				data
			) as OrderedSet<AppTagItemEntity>;
			const tags = tagsSet.toList().toArray();
			if (tags.length === 0 && value === NoopValue) {
				return true;
			}

			return (
				!!tags &&
				!!tags.find((v) => checkStringEquality(v.label, value, filterOperation))
			);
		};
		if (
			target === DxFilterElem.Header ||
			target === DxFilterElem.FilterRow ||
			target === DxFilterElem.FilterBuilder
		) {
			return [selector, DxOperations.Equal, true];
		}
		// eslint-disable-next-line prefer-rest-params
		return column.defaultCalculateFilterExpression.apply(this, arguments);
	}

	public calculateActionByHeaderFilterExpression(
		value: string,
		filterOperation: string,
		target: string
	): unknown {
		const column = this as any;
		const selector = (data: ConnectorItemEntity): boolean => {
			const entity = column.calculateCellValue(data) as AvatarEntity;
			if (entity === null && value === NoopValue) {
				return true;
			}
			if (!entity) {
				return false;
			}
			const { name } = entity;
			return !!name && checkStringEquality(name, value, filterOperation);
		};
		if (
			target === DxFilterElem.Header ||
			target === DxFilterElem.FilterRow ||
			target === DxFilterElem.FilterBuilder
		) {
			return [selector, DxOperations.Equal, true];
		}
		// eslint-disable-next-line prefer-rest-params
		return column.defaultCalculateFilterExpression.apply(this, arguments);
	}

	private async deleteSelected(
		selectedItem: ConnectorItemEntity | undefined = undefined
	): Promise<void> {
		let selected: Array<number>;
		let elementLabel = '';

		if (!selectedItem) {
			if (!this.grid) {
				return;
			}
			const selectedRowKeys = this.grid.selectedRowKeys as Array<string>;
			const selectedConnectors = await firstValueFrom(
				this.connectorSelector.getSelectedConnectors$(selectedRowKeys)
			);

			selected = selectedConnectors.map((k) => k.id);
			elementLabel =
				selectedConnectors.length === 1 ? selectedConnectors[0].label : '';
		} else {
			selected = [selectedItem.id];
			elementLabel = selectedItem.label;
		}

		this.modalService.openPopup<IConnectorActionModalData>(
			ConnectorActionPopupComponent,
			{
				title: $localize`:i18n=@@connector.action.popup.delete.header:`,
				subTitle: '',
				connectorId: selected,
				element: elementLabel,
				action: ConnectorAction.Delete,
			},
			{
				disableClose: true,
			}
		);
	}

	private handleSubscriptions(): void {
		this.actions$
			.pipe(
				takeUntil(this.onDestroy$),
				ofType(
					deleteConnectorSuccess,
					bulkDeleteConnectorsSuccess,
					deleteConnectorFail,
					bulkDeleteConnectorsFail
				),
				tap(() => this.refresh())
			)
			.subscribe();
	}

	private toggleActivationStatus(connector: ConnectorItemEntity): void {
		this.store.dispatch(
			toggleConnectorStatus({
				connectorId: connector.id,
				connectorType: connector.type,
			})
		);
	}

	private async bootstrapIcons(): Promise<void> {
		const actions = [
			ActionIcons.ColumnExpositionChooser,
			ActionIcons.Delete,
			ActionIcons.Filter,
		];
		const actionMap = await firstValueFrom(
			this.actionIcons.getFromRegistry(...actions)
		);
		this.panelActions = [
			{
				order: 0,
				command: (): void => {
					this.grid?.instance.showColumnChooser();
				},
				iconName: actionMap.get(ActionIcons.ColumnExpositionChooser)?.iconName,
				description: '',
				tooltip: $localize`:i18n=@@connector.list.columnSelection:`,
				typed: '',
			},
			{
				order: 2,
				command: (): Promise<void> => this.deleteSelected(),
				iconName: actionMap.get(ActionIcons.Delete)?.iconName,
				description: '',
				tooltip: $localize`:i18n=@@connector.list.deleteSelection:`,
				typed: 'plain-context-action',
			},
		];
	}
}
